import { bunja } from 'bunja';
import { atom } from 'jotai';
import { atomWithDefault } from 'jotai/utils';

import { abTestBunja } from '@flipster/features/src/shared/ab-test';
import { authBunja } from '@flipster/features/src/shared/auth/state';
import { rawInstrumentsKeyBySymbolAtom } from '@flipster/features/src/shared/snapshot/state';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { getLocalStorage, setLocalStorage } from '@flipster/utils/localStorage';
import segment from '@flipster/utils/segment';
import { TradeEvent } from '@flipster/utils/segment/events/trade';

import { tradeSymbolBunja } from '@web/app/[locale]/(web)/trade/_misc/symbol';

export type OrderbookDisplayMode = 'amount' | 'qty';

/**
 * 주문 폼과 오더북이 공유하는 unit 선호도 (Base Qty vs Quote/USDT).
 * 심볼별 + 계정별로 영구 저장되며, 폼/오더북 어느 쪽에서 변경하든 양쪽이 즉시 반영된다.
 *
 * 매핑:
 * - `'qty'`  ↔ Order book in Qty            ↔ Order by Qty (positionSize+qty / spot quantity)
 * - `'amount'` ↔ Order book in USDT         ↔ Order by Margin (USDT) 또는 Position size in USDT
 */
export const tradeUnitBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom } = bunja.use(authBunja);
  const { symbol, symbolInfoExistsAtom, isSpotAtom } = bunja.use(tradeSymbolBunja);

  const getStorageKey = (userId: number) => `flipster.configs.v1.${userId}.orderbookDisplayMode.${symbol}` as const;

  const tradeUnitAtom = atomWithDefault<OrderbookDisplayMode>((get) => {
    const defaultMode: OrderbookDisplayMode = 'qty';
    const user = get(userAtom);
    if (!user?.userId) return defaultMode;
    const storedValue = getLocalStorage(getStorageKey(user.userId));
    const isValid = storedValue === 'amount' || storedValue === 'qty';
    return isValid ? storedValue : defaultMode;
  });

  const changeTradeUnit = (mode: OrderbookDisplayMode) => {
    const prevMode = store.get(tradeUnitAtom);
    if (prevMode === mode) return;

    store.set(tradeUnitAtom, mode);

    const user = store.get(userAtom);
    if (user?.userId) {
      setLocalStorage(getStorageKey(user.userId), mode);
    }

    if (!store.get(symbolInfoExistsAtom)) return;
    const isSpot = store.get(isSpotAtom);
    segment.track(TradeEvent.OrderBookUnitSelectClick, {
      symbol,
      previous_unit: prevMode === 'amount' ? 'QUOTE' : 'BASE',
      unit_selected: mode === 'amount' ? 'QUOTE' : 'BASE',
      trade_type: isSpot ? 'spot' : 'perpetual',
    });
  };

  return { tradeUnitAtom, changeTradeUnit };
});
tradeUnitBunja.debugLabel = 'tradeUnitBunja';

export const orderbookStateBunja = bunja(() => {
  const store = bunja.use(JotaiStoreScope);
  const { userAtom } = bunja.use(authBunja);
  const { symbol, symbolInfoExistsAtom, isSpotAtom } = bunja.use(tradeSymbolBunja);
  const { getAbFlagAtom } = bunja.use(abTestBunja);
  const { tradeUnitAtom, changeTradeUnit } = bunja.use(tradeUnitBunja);

  const mountedAtom = atom(false);
  const defaultWhileAtom = atom((get) => !get(mountedAtom));
  bunja.effect(() => store.set(mountedAtom, true));

  const isSpotLimitOrderEnabledAtom = getAbFlagAtom('25q4-spot-limit-order', 'on', { defaultWhileAtom });

  const isShowOrderbookAtom = atom((get) => {
    if (!get(symbolInfoExistsAtom)) return false;
    if (get(isSpotAtom)) return get(isSpotLimitOrderEnabledAtom);
    return true;
  });

  // --- Display mode (amount/qty) ---
  // 폼과 공유하는 unit 선호도. tradeUnitBunja가 단일 진실 원천.
  const displayModeAtom = tradeUnitAtom;
  const changeDisplayMode = changeTradeUnit;

  // --- Tick size depth (aggregation) ---

  const tickSizeLevelsAtom = atom((get) => {
    const instruments = get(rawInstrumentsKeyBySymbolAtom);
    const instrument = instruments?.[symbol];
    if (!instrument) return [];
    return instrument.tickSizeDisplayLevels ?? [instrument.tickSize];
  });

  const baseTickSizeAtom = atom((get) => {
    const instruments = get(rawInstrumentsKeyBySymbolAtom);
    return instruments?.[symbol]?.tickSize ?? '0';
  });

  const getTickSizeStorageKey = (userId: number) =>
    `flipster.configs.v1.${userId}.orderbookTickSize.${symbol}` as const;

  const selectedTickSizeAtom = atomWithDefault<string>((get) => {
    const levels = get(tickSizeLevelsAtom);
    const baseTickSize = get(baseTickSizeAtom);
    if (levels.length === 0) return baseTickSize;

    const user = get(userAtom);
    if (user?.userId) {
      const stored = getLocalStorage(getTickSizeStorageKey(user.userId));
      const storedStr = String(stored ?? '');
      if (storedStr && levels.includes(storedStr)) return storedStr;
    }

    return baseTickSize;
  });

  const changeSelectedTickSize = (tickSize: string) => {
    store.set(selectedTickSizeAtom, tickSize);
    const user = store.get(userAtom);
    if (user?.userId) {
      setLocalStorage(getTickSizeStorageKey(user.userId), tickSize);
    }
  };

  return {
    isShowOrderbookAtom,
    displayModeAtom,
    changeDisplayMode,
    tickSizeLevelsAtom,
    baseTickSizeAtom,
    selectedTickSizeAtom,
    changeSelectedTickSize,
  };
});
orderbookStateBunja.debugLabel = 'orderbookStateBunja';
